'use client';

import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Coins, Flame, Snowflake, Trophy } from 'lucide-react';

import { useT } from '@/hooks';
import { colors } from '@/styles/theme';
import type { StreakStatus } from '@/types/streak.types';

interface StreakStatsRowProps {
  data: StreakStatus;
}

/**
 * StreakStatsRow
 * The four numbers that summarize where a user stands, same Paper-tile
 * pattern as ReferralStats so the two program pages feel like one system.
 */
export const StreakStatsRow: React.FC<StreakStatsRowProps> = ({ data }) => {
  const { t } = useT();

  const stats = [
    {
      label: t('streaks.statCurrent', { defaultValue: 'Current streak' }),
      value: `${data.consecutiveDays}d`,
      icon: Flame,
      color: '#e8b046',
      bg: 'rgba(249,115,22,0.12)',
    },
    {
      label: t('streaks.statLongest', { defaultValue: 'Longest streak' }),
      value: `${data.longestConsecutiveDays}d`,
      icon: Trophy,
      color: '#f5b301',
      bg: 'rgba(245,179,1,0.12)',
    },
    {
      label: t('streaks.statPoints', { defaultValue: 'Points balance' }),
      value: data.pointsBalance.toLocaleString(),
      icon: Coins,
      color: colors.accent.purple[500],
      bg: 'rgba(168,85,247,0.12)',
    },
    {
      label: t('streaks.statFreezes', { defaultValue: 'Freezes available' }),
      value: String(data.freezesAvailable),
      icon: Snowflake,
      color: colors.info.main,
      bg: 'rgba(91, 140, 255, 0.12)',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 2,
      }}
    >
      {stats.map((stat) => (
        <Paper
          key={stat.label}
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
            p: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: stat.bg,
              color: stat.color,
              mb: 1.5,
            }}
          >
            <stat.icon size={20} />
          </Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
            {stat.value}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {stat.label}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};
